import React, { useState } from "react";
import {
  ActivityIndicator,
  Alert,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { MaterialIcons } from "@expo/vector-icons";
import * as Sentry from "@sentry/react-native";

import UserNotificationBaseItem from "./UserNotificationBaseItem";

import {
  type UserNotification,
  type UserNotificationNewFollowerData,
} from "@/schemas/user/notification";
import { themes } from "@/Components/Styling/themeConfigs";
import RelationshipService from "@/services/user/relationship";
import type { AppUserSession } from "@/schemas/auth/session";

const currentTheme = themes.morning;

type Props = {
  session: AppUserSession;
  userNotification: UserNotification;
  handleMarkNotificationAsRead: () => void;
};

function UserNotificationNewFollowerItem({
  session,
  userNotification,
  handleMarkNotificationAsRead,
}: Props): React.JSX.Element {
  const [isLoading, setIsLoading] = useState(false); // for follow back button activity indicator
  const [hasFollowedBack, setHasFollowedBack] = useState(false);

  const notificationData =
    userNotification.data as UserNotificationNewFollowerData;
  const profilePreview = notificationData.newFollower.followedBy;

  const handleFollowButtonPress = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const connectionService = new RelationshipService(session);
      await connectionService.toFollow(profilePreview.username);

      setHasFollowedBack(true);
    } catch (error) {
      Sentry.captureException(error);
      Alert.alert("Something went wrong", "Please try again later.", [
        { text: "OK" },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // TODO?: https://github.com/dow-labs/Kai-Mobile/pull/700#discussion_r2080258202
  return (
    <UserNotificationBaseItem
      renderRightComponent={(markAsRead) => {
        if (hasFollowedBack || notificationData.newFollower.isFollowing) {
          return <></>;
        }
        return (
          <Pressable
            disabled={isLoading}
            onPress={() => {
              markAsRead();
              !hasFollowedBack && handleFollowButtonPress();
            }}
            style={styles.baseButton}
          >
            {isLoading ? (
              <ActivityIndicator size="small" />
            ) : (
              <View>
                <Text style={styles.baseButtonText}>Follow Back</Text>
              </View>
            )}
          </Pressable>
        );
      }}
      userNotification={userNotification}
      profile={profilePreview}
      handleMarkNotificationAsRead={handleMarkNotificationAsRead}
      notificationTypeIconComponent={
        <MaterialIcons name="person-add-alt" size={17} />
      }
      notificationDescription={"followed you"}
    />
  );
}

const styles = StyleSheet.create({
  baseButton: {
    borderRadius: 20,
    marginLeft: "auto",
    paddingVertical: 5,
    paddingHorizontal: 15,
    borderWidth: 1,
    minHeight: 40,
    minWidth: 80,
    alignItems: "center",
    justifyContent: "center",
    borderColor: "black",
  },
  baseButtonText: {
    fontSize: currentTheme.typography.bodySmall.size.mobile,
    fontWeight: 600,
    color: "black",
  },
});

export default UserNotificationNewFollowerItem;
